import { useAuthSession } from '../lib/authSession'
import DocumentLink from './documents/DocumentLink'
import DocumentPdf from './documents/DocumentPdf'
import './documents/reader.css'
import { useState, useEffect } from 'react'
import { apiFetch, ApiError } from '../lib/api'
import { cleanEngineText } from '../lib/officerFormat'
import type { TaskQueueItem } from '../lib/tasks'
import ManualEntry from './ManualEntry'
import { HelpDot } from './guidance/Guidance'

/* Take-over / resolution pane — IO ledger screen `tasks_takeover`.
 * The pipeline stopped before producing anything reviewable, or a confirmed
 * write failed to land; the officer takes over. Three task shapes route here:
 *
 *   document_stuck (target = dead-lettered PendingEvent) →
 *     Retry (re-mints the event) / Take over by hand (manual entry) /
 *     Discard (reason required; the dead-letter row stays for audit).
 *
 *   dead_letter_review (target = proposal group whose WRITE failed) →
 *     Retry the whole group, OR exclude the poison leg(s) and re-confirm the
 *     rest (G1 / #808 — exclude_proposal_ids stamps the excluded proposals
 *     rejected before the savepoint-atomic write re-runs) / Discard.
 *     WI-9: the retry re-runs the write sequence SYNCHRONOUSLY server-side
 *     and the pane reports the honest outcome — landed (succeeded/partial)
 *     or "retried, dead-lettered again on <reason>" with the task kept open
 *     — never a silent requeue loop.
 *
 *   look_through_incomplete (target = fund with no confirmed target) →
 *     guidance + a deep-link to the chain review, where confirming a target
 *     auto-resolves the task. No stuck-action — this is not a dead-letter.
 */

interface Leg {
  id: string
  proposal_type: string
  status: string
  proposed_data: Record<string, unknown> | null
}

interface ProposalGroupLite {
  id: string
  document_id: string | null
  proposals: Leg[]
}

/** One pending confirm_target election (GET /api/proposals/target-elections). */
interface TargetElection {
  proposal_id: string
  proposal_group_id: string
  entity_id: string | null
  entity_name: string
  /** Carried so the officer can see WHAT he is stopping at — a collective
   *  vehicle is not an asset (Art. 21 depth). */
  entity_nature?: string | null
  fund_id: string | null
  fund_ids?: string[]
  reasoning: string | null
}

/** WI-9 — honest stuck-action outcomes; see StuckActionResponse server-side. */
interface StuckActionResult {
  task_id: string
  status: string
  retried_event_id: string | null
  outcome:
    | 'requeued'
    | 'succeeded'
    | 'partial'
    | 'dead_lettered_again'
    | 'resolution_question'
    | 'discarded'
    | null
  failure_reason: string | null
  /** Set only for outcome='resolution_question' — the plausible-twin question
   *  the officer answers on the review queue. Never a failure. */
  resolution_question: string | null
  written_count: number | null
}

function errText(e: unknown): string {
  if (e instanceof ApiError) {
    const d = (e.body as { detail?: unknown } | undefined)?.detail
    if (typeof d === 'string') return d
    return `${e.statusText} (${e.status})`
  }
  return e instanceof Error ? e.message : 'request failed'
}

/** Best-effort document id for the source viewer: dead-letter groups carry it
 *  on the group; document_stuck tasks stash it on task_data. */
function taskDocumentId(task: TaskQueueItem, group: ProposalGroupLite | null): string | null {
  if (group?.document_id) return group.document_id
  const d = task.task_data as Record<string, unknown> | null
  const raw = d?.document_id
  return typeof raw === 'string' ? raw : null
}

/** A short, human label for one proposal leg. */
function legLabel(leg: Leg): string {
  const d = leg.proposed_data ?? {}
  const object = leg.proposal_type.replace(/^(create|update|flag|link)_/, '').replace(/_/g, ' ')
  const name =
    (typeof d.instrument_name === 'string' && d.instrument_name) ||
    (typeof d.holder_entity_name === 'string' && d.holder_entity_name) ||
    (typeof d.entity_name === 'string' && d.entity_name) ||
    (typeof d.transaction_type === 'string' && d.transaction_type) ||
    null
  return name ? `${object} · ${name}` : object
}

/** The dispatcher writes what/where/why into the task description —
 * surface it as labelled lines instead of a text blob. */
function DescriptionFacts({ description }: { description: string | null }) {
  if (!description)
    return (
      <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
        No failure detail recorded.
      </div>
    )
  const lines = description.split('\n').filter(Boolean)
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', gap: '5px 14px' }}>
      {lines.map((l, i) => {
        const m = l.match(/^([^:]{2,40}):\s*(.*)$/)
        return m ? (
          <span key={i} style={{ display: 'contents' }}>
            <span style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
              {m[1].toLowerCase()}
            </span>
            <span
              className="mono"
              style={{ fontSize: 'var(--text-12)', fontWeight: 600, overflowWrap: 'anywhere' }}
            >
              {cleanEngineText(m[2])}
            </span>
          </span>
        ) : (
          <span key={i} style={{ gridColumn: '1 / -1', fontSize: 'var(--text-12)' }}>
            {cleanEngineText(l)}
          </span>
        )
      })}
    </div>
  )
}

/** Authed PDF byte-stream viewer. */
function SourcePdf({ documentId }: { documentId: string | null }) {
  const session = useAuthSession()
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  useEffect(() => {
    setPage(1)
    setTotal(0)
  }, [documentId, session])
  if (!documentId)
    return <p className="reader-note">No source document is attached to this task.</p>
  return (
    <section
      style={{
        flex: 1,
        minWidth: 0,
        alignSelf: 'stretch',
        minHeight: 400,
        maxHeight: '75vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div className="reader-document-links">
        <DocumentLink
          document={{ document_id: documentId, page }}
          context={{ returnTo: window.location.pathname + window.location.search }}
        />
        <button type="button" disabled={page <= 1} onClick={() => setPage((n) => n - 1)}>
          Previous page
        </button>
        <span>
          {page} / {total || '…'}
        </span>
        <button
          type="button"
          disabled={!total || page >= total}
          onClick={() => setPage((n) => n + 1)}
        >
          Next page
        </button>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>
        <DocumentPdf
          documentId={documentId}
          session={session}
          page={page}
          zoom={1}
          annotations={[]}
          selected={null}
          onSelect={() => {}}
          onPages={setTotal}
        />
      </div>
    </section>
  )
}

export default function TakeOverPane({
  task,
  onResolved,
}: {
  task: TaskQueueItem
  onResolved: () => void
}) {
  const isStuck = task.task_type === 'document_stuck'
  const isDeadLetter = task.task_type === 'dead_letter_review'
  const isLookThrough = task.task_type === 'look_through_incomplete'

  const [manualOpen, setManualOpen] = useState(false)
  const [discardOpen, setDiscardOpen] = useState(false)
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)

  // G1 — dead-letter group legs + per-leg exclusions.
  const [group, setGroup] = useState<ProposalGroupLite | null>(null)
  const [excluded, setExcluded] = useState<Set<string>>(new Set())
  // WI-9 — the re-run dead-lettered AGAIN; carries the fresh write error.
  const [retryFail, setRetryFail] = useState<string | null>(null)

  // Value gap 2 (2026-07-24): the look-through task's own loop, in-pane.
  // Pending confirm_target elections for this fund; the officer's YES is
  // the existing per-group /confirm POST (approve → is_target=true), the
  // NO is a reject with a reason. Once a target is confirmed the chain
  // re-walks and this task auto-resolves.
  const [elections, setElections] = useState<TargetElection[]>([])
  const [confirmingId, setConfirmingId] = useState<string | null>(null)
  const [rejectingId, setRejectingId] = useState<string | null>(null)
  const [rejectReason, setRejectReason] = useState('')
  const [decidedNames, setDecidedNames] = useState<string[]>([])
  const [electionsError, setElectionsError] = useState<string | null>(null)

  function loadGroup() {
    if (!isDeadLetter || !task.target_id) return
    apiFetch<ProposalGroupLite>(`/api/proposals/${task.target_id}`)
      .then((g) => setGroup(g))
      .catch(() => {
        /* group may be unreadable — retry still works without the picker */
      })
  }

  function loadElections() {
    if (!isLookThrough) return
    const qs = task.fund_id ? `?fund_id=${encodeURIComponent(task.fund_id)}` : ''
    setElectionsError(null)
    apiFetch<{ elections: TargetElection[] }>(`/api/proposals/target-elections${qs}`)
      .then((d) => setElections(d.elections ?? []))
      .catch((e) => {
        // Swallowing this told the officer "no candidate is ready to confirm
        // yet" — a claim about the RECORD — when the truth was that the
        // request failed. Same silent-catch class as the blank Funds page.
        setElectionsError(e instanceof Error ? e.message : 'could not be loaded')
      })
  }

  useEffect(() => {
    setGroup(null)
    setExcluded(new Set())
    setRetryFail(null)
    setElections([])
    setConfirmingId(null)
    setRejectingId(null)
    setRejectReason('')
    setDecidedNames([])
    loadGroup()
    loadElections()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDeadLetter, isLookThrough, task.target_id, task.fund_id])

  async function decideElection(e: TargetElection, action: 'approve' | 'reject', reason?: string) {
    setBusy(true)
    setError(null)
    try {
      await apiFetch(`/api/proposals/${e.proposal_group_id}/confirm`, {
        method: 'POST',
        body: JSON.stringify({
          decisions: [
            {
              proposal_id: e.proposal_id,
              action,
              ...(action === 'reject' && reason ? { rejection_justification: reason } : {}),
            },
          ],
        }),
      })
      setElections((prev) => prev.filter((x) => x.proposal_id !== e.proposal_id))
      if (action === 'approve') setDecidedNames((prev) => [...prev, e.entity_name])
      setConfirmingId(null)
      setRejectingId(null)
      setRejectReason('')
    } catch (err) {
      setError(errText(err))
    } finally {
      setBusy(false)
    }
  }

  const documentId = taskDocumentId(task, group)
  // Legs the re-run would write. A dead-lettered group's proposals are
  // already stamped 'confirmed' (process_decisions runs BEFORE the write
  // sequence), so 'pending' alone rendered an empty picker on every real
  // dead-letter (WI-9). Rejected legs are out — the write sequence skips
  // them (#806).
  const retryLegs = (group?.proposals ?? []).filter(
    (p) => p.status === 'pending' || p.status === 'confirmed'
  )

  function toggleExcluded(id: string) {
    setExcluded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  async function act(action: 'retry' | 'discard', excludeIds?: string[]) {
    setBusy(true)
    setError(null)
    setRetryFail(null)
    try {
      const result = await apiFetch<StuckActionResult>(`/api/tasks/${task.task_id}/stuck-action`, {
        method: 'POST',
        body: JSON.stringify({
          action,
          note: note || undefined,
          ...(excludeIds && excludeIds.length > 0 ? { exclude_proposal_ids: excludeIds } : {}),
        }),
      })
      if (action !== 'retry') {
        setDone('Discarded with your reason. The dead-letter record stays in the audit trail.')
      } else if (result.outcome === 'dead_lettered_again') {
        // Honest failure — the re-run happened and dead-lettered AGAIN.
        // The task stays open with its description refreshed; surface the
        // fresh reason and keep the pane live (exclude a leg / discard).
        setRetryFail(result.failure_reason || 'no failure detail recorded')
        setExcluded(new Set())
        loadGroup() // excluded legs are now rejected — refresh the picker
      } else if (result.outcome === 'resolution_question') {
        // Not a failure — the write was HELD because the new object looks
        // like a duplicate of one already in the record. The group is back
        // on the review queue with the merge question; this take-over task
        // is done.
        setDone(
          `Held for a resolution question — ${
            result.resolution_question || 'this looks like a duplicate of a record object'
          }. The group is back in the review queue: answer merge or keep-separate there and it writes. Task resolved.`
        )
      } else if (result.outcome === 'succeeded') {
        setDone(
          result.written_count === 0
            ? 'Nothing left to do — this write had already landed. Task resolved.'
            : `The write landed — ${result.written_count} record row${
                result.written_count === 1 ? '' : 's'
              } written. Task resolved.`
        )
      } else if (result.outcome === 'partial') {
        setDone(
          `The write landed without ${excludeIds?.length ?? 0} excluded leg${
            (excludeIds?.length ?? 0) === 1 ? '' : 's'
          } (stamped rejected) — ${result.written_count ?? 0} record row${
            result.written_count === 1 ? '' : 's'
          } written. Task resolved.`
        )
      } else {
        // 'requeued' — async replay (document_stuck / special group shapes).
        setDone(
          'Event re-queued — the pipeline picks it up again. If it dead-letters a second time, a fresh take-over task appears here.'
        )
      }
    } catch (e) {
      setError(errText(e))
    } finally {
      setBusy(false)
    }
  }

  if (done)
    return (
      <div className="vcard" style={{ flex: 1, alignSelf: 'flex-start', padding: 22 }}>
        <div
          data-testid="takeover-done"
          style={{ fontSize: 'var(--text-13)', color: 'var(--verified)', fontWeight: 600 }}
        >
          ✓ {done}
        </div>
        <button className="vbtn pri" style={{ marginTop: 16 }} onClick={onResolved}>
          Next task →
        </button>
      </div>
    )

  if (manualOpen)
    return (
      <div style={{ flex: 1 }}>
        <button className="vbtn" style={{ marginBottom: 12 }} onClick={() => setManualOpen(false)}>
          ← back to take-over
        </button>
        <div style={{ fontSize: 'var(--text-13)', fontWeight: 700, marginBottom: 2 }}>
          Build the chain by hand
        </div>
        <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginBottom: 14 }}>
          The pipeline couldn’t, so you do — entity → instrument → position → trade. Each object is
          proposed into the record-keeping queue and books when you approve it there. When you’re
          done, discard the stuck task.
        </div>
        <ManualEntry fundId={task.fund_id || undefined} />
      </div>
    )

  const heading = isStuck
    ? 'The pipeline stopped on this document'
    : isDeadLetter
      ? 'A confirmed write failed to land'
      : 'This fund’s look-through has no confirmed target'
  const subheading = isStuck
    ? 'nothing reviewable was produced — retry the step or take the work over by hand'
    : isDeadLetter
      ? 'your approval was recorded, but writing it into the record failed and was set aside — retry it, or exclude the failing change and re-confirm the rest'
      : 'the ownership chain stays open until its end point is confirmed — confirm it in the chain review to clear this'

  return (
    <div style={{ flex: 1, display: 'flex', gap: 16, alignItems: 'stretch', minWidth: 0 }}>
      {/* LEFT — source document, when we have one */}
      {(isStuck || isDeadLetter) && (
        <div style={{ flex: 1, minWidth: 0, display: 'flex' }}>
          <SourcePdf documentId={documentId} />
        </div>
      )}

      {/* MIDDLE/RIGHT — context + record impact + actions */}
      <div
        style={{
          width: isLookThrough ? undefined : 460,
          flex: isLookThrough ? 1 : undefined,
          display: 'flex',
          flexDirection: 'column',
          gap: 12,
          overflowY: 'auto',
        }}
      >
        <div className="vcard" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--line)' }}>
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
              {heading} <HelpDot k="pane.takeover" />
            </div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', marginTop: 2 }}>
              {subheading}
            </div>
          </div>
          <div style={{ padding: '14px 18px' }}>
            {error && (
              <div style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', marginBottom: 10 }}>
                {error}
              </div>
            )}
            {isDeadLetter ? (
              <>
                {/* Copy pass (glossary 2026-07-24): the dispatcher's account is
                    engine prose (group/entity UUIDs, record-model citations) —
                    lead with a human sentence, keep the engine's own words
                    behind a disclosure for debugging. */}
                <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
                  The record refused this write — usually a value that conflicts with something
                  already confirmed. The engine's own account of the failure is below.
                </div>
                <details data-testid="engine-failure-detail" style={{ marginTop: 8 }}>
                  <summary
                    style={{ fontSize: 'var(--text-12)', color: 'var(--faint)', cursor: 'pointer' }}
                  >
                    Technical failure detail
                  </summary>
                  <div style={{ marginTop: 8 }}>
                    <DescriptionFacts description={task.description} />
                  </div>
                </details>
              </>
            ) : (
              <DescriptionFacts description={task.description} />
            )}
          </div>
        </div>

        {/* RECORD IMPACT — the legs that would be written (dead-letter). */}
        {isDeadLetter && retryLegs.length > 0 && (
          <div className="vcard" style={{ padding: '14px 18px' }}>
            <div
              style={{
                fontSize: 'var(--text-12)',
                textTransform: 'uppercase',
                letterSpacing: '.08em',
                color: 'var(--faint)',
                marginBottom: 8,
              }}
            >
              Record impact · {retryLegs.length} leg{retryLegs.length === 1 ? '' : 's'}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {retryLegs.map((leg) => (
                <div
                  key={leg.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    fontSize: 'var(--text-12)',
                    opacity: excluded.has(leg.id) ? 0.5 : 1,
                  }}
                >
                  <span
                    className="tag"
                    style={{ textDecoration: excluded.has(leg.id) ? 'line-through' : 'none' }}
                  >
                    {leg.proposal_type.replace(/_/g, ' ')}
                  </span>
                  <span
                    style={{
                      textTransform: 'capitalize',
                      textDecoration: excluded.has(leg.id) ? 'line-through' : 'none',
                    }}
                  >
                    {legLabel(leg)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── document_stuck actions ── */}
        {isStuck && (
          <>
            <div className="vcard" style={{ padding: 16 }}>
              <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Retry the step</div>
              <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
                Re-queues the exact failed event. Right call after a transient failure (timeout,
                rate limit) or a fix that landed since.
              </div>
              <button
                className="vbtn pri"
                style={{ marginTop: 12 }}
                onClick={() => act('retry')}
                disabled={busy}
              >
                Retry
              </button>
            </div>
            <div className="vcard" style={{ padding: 16 }}>
              <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Take over by hand</div>
              <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
                Build the chain yourself from the document — entity → instrument → position → trade.
                No dead ends: what the pipeline couldn't do, you can.
              </div>
              <button
                className="vbtn"
                style={{ marginTop: 12 }}
                onClick={() => setManualOpen(true)}
              >
                Enter manually…
              </button>
            </div>
          </>
        )}

        {/* ── dead_letter_review actions (G1) ── */}
        {isDeadLetter && (
          <div className="vcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Re-confirm the write</div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              A failed write changes nothing — the record was left untouched, so it is safe to
              re-run. The re-run happens right here: you see whether it landed. If one change can
              never go through, exclude it: it is marked rejected and the rest is re-confirmed.
            </div>
            {retryFail && (
              <div
                data-testid="deadletter-retry-failed"
                style={{
                  marginTop: 10,
                  padding: '8px 10px',
                  borderRadius: 7,
                  border: '1px solid var(--gap)',
                  fontSize: 'var(--text-12)',
                }}
              >
                <span style={{ fontWeight: 700, color: 'var(--gap)' }}>
                  Retried — the write failed again:
                </span>{' '}
                <span className="mono" style={{ overflowWrap: 'anywhere' }}>
                  {cleanEngineText(retryFail)}
                </span>
                <div style={{ marginTop: 4, color: 'var(--muted)' }}>
                  The task stays open. Exclude the leg that can’t land and retry, supersede the
                  conflicting value first, or discard with a reason.
                </div>
              </div>
            )}
            {retryLegs.length > 0 && (
              <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ fontSize: 'var(--text-12)', color: 'var(--faint)' }}>
                  Exclude a leg to drop it from the re-run:
                </div>
                {retryLegs.map((leg) => (
                  <label
                    key={leg.id}
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      fontSize: 'var(--text-12)',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={excluded.has(leg.id)}
                      onChange={() => toggleExcluded(leg.id)}
                      data-testid="exclude-leg"
                    />
                    <span style={{ textTransform: 'capitalize' }}>{legLabel(leg)}</span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              <button
                className="vbtn pri"
                onClick={() => act('retry', Array.from(excluded))}
                disabled={busy || (retryLegs.length > 0 && excluded.size === retryLegs.length)}
                data-testid="deadletter-retry"
              >
                {excluded.size > 0
                  ? `Re-confirm without ${excluded.size} leg${excluded.size === 1 ? '' : 's'}`
                  : 'Retry whole group'}
              </button>
              {retryLegs.length > 0 && excluded.size === retryLegs.length && (
                <span
                  style={{ fontSize: 'var(--text-12)', color: 'var(--gap)', alignSelf: 'center' }}
                >
                  can’t exclude every leg — discard instead
                </span>
              )}
            </div>
          </div>
        )}

        {/* ── look_through_incomplete: confirm the target IN-PANE (value
            gap 2 — the CTA used to land on an empty chain review). ── */}
        {isLookThrough && (
          <div className="vcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>
              Confirm where the chain ends
            </div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              The look-through walks down the ownership chain until it reaches the asset it can stop
              at. This fund’s chain has no confirmed end point yet. Confirm one below — the task
              clears automatically once it is confirmed.
            </div>
            {decidedNames.length > 0 && (
              <div
                data-testid="target-confirmed-note"
                style={{
                  marginTop: 10,
                  fontSize: 'var(--text-12)',
                  color: 'var(--muted)',
                  fontWeight: 600,
                }}
              >
                Decision submitted for {decidedNames.join(', ')}. Processing can still require a
                fresh review.
                <button
                  type="button"
                  className="cn-btn"
                  disabled={busy}
                  onClick={loadElections}
                  style={{ marginLeft: 8 }}
                >
                  Refresh candidates
                </button>
              </div>
            )}
            {electionsError && (
              <div
                data-testid="target-elections-error"
                style={{ marginTop: 10, fontSize: 'var(--text-12)', color: 'var(--gap)' }}
              >
                The candidate list could not be loaded — {electionsError}. This does not mean there
                is nothing to confirm; open the chain review below.
              </div>
            )}
            {!electionsError && elections.length === 0 && decidedNames.length === 0 && (
              <div
                data-testid="no-target-elections"
                style={{ marginTop: 10, fontSize: 'var(--text-12)', color: 'var(--faint)' }}
              >
                No candidate is ready to confirm yet — the chain may still be under review. The
                chain review below shows what is pending.
              </div>
            )}
            {elections.map((e) => (
              <div
                key={e.proposal_id}
                data-testid="target-election-row"
                style={{
                  marginTop: 10,
                  padding: '10px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: 8,
                }}
              >
                <div style={{ fontSize: 'var(--text-12)', fontWeight: 700 }}>{e.entity_name}</div>
                <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 2 }}>
                  Confirm {e.entity_name} as the chain’s target — the look-through stops here.
                  Recorded permanently under your name.
                </div>
                {e.entity_nature === 'collective_vehicle' && (
                  /* Ale ruling 2026-07-29: a fund CAN be a legitimate chain
                     end. In a target limited partnership management is
                     delegated, the depositary has no control, and the target
                     fund has its own depositary — so the walk stopping there is
                     the normal case, not a defect. What is forbidden is a fund
                     ending its OWN chain (chain head is not chain end), which
                     the target_election guard enforces server-side. So this
                     note informs the decision; it must not read as a warning
                     against a routine one. */
                  <div
                    data-testid="target-is-vehicle"
                    style={{
                      marginTop: 6,
                      fontSize: 'var(--text-12)',
                      color: 'var(--gap)',
                      display: 'flex',
                      gap: 6,
                    }}
                  >
                    <span>⚠</span>
                    <span>
                      {e.entity_name} is itself a collective vehicle. A chain can legitimately end
                      at one — in a target limited partnership the manager is delegated, there is no
                      control, and the target has its own depositary. Confirm if that is the case
                      here. What it cannot be is the end of <em>its own</em> chain.
                    </span>
                  </div>
                )}
                {confirmingId !== e.proposal_id && rejectingId !== e.proposal_id && (
                  <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                    <button
                      className="vbtn pri"
                      data-testid="target-confirm"
                      disabled={busy}
                      onClick={() => setConfirmingId(e.proposal_id)}
                    >
                      Confirm as target…
                    </button>
                    <button
                      className="vbtn"
                      data-testid="target-reject"
                      disabled={busy}
                      onClick={() => {
                        setRejectingId(e.proposal_id)
                        setConfirmingId(null)
                      }}
                    >
                      Not the target…
                    </button>
                  </div>
                )}
                {confirmingId === e.proposal_id && (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)' }}>
                      The chain will terminate at {e.entity_name}. This is written to the record at
                      once.
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button
                        className="vbtn pri"
                        data-testid="target-confirm-commit"
                        disabled={busy}
                        onClick={() => decideElection(e, 'approve')}
                      >
                        Yes — confirm target
                      </button>
                      <button
                        className="vbtn"
                        disabled={busy}
                        onClick={() => setConfirmingId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
                {rejectingId === e.proposal_id && (
                  <div style={{ marginTop: 8 }}>
                    <input
                      autoFocus
                      placeholder="why does the chain continue past this entity? (required)"
                      value={rejectReason}
                      onChange={(ev) => setRejectReason(ev.target.value)}
                      style={{
                        width: '100%',
                        font: 'inherit',
                        fontSize: 'var(--text-12)',
                        padding: '7px 10px',
                        border: '1.5px solid var(--line)',
                        borderRadius: 7,
                      }}
                    />
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button
                        className="vbtn pri"
                        data-testid="target-reject-commit"
                        disabled={busy || !rejectReason.trim()}
                        onClick={() => decideElection(e, 'reject', rejectReason.trim())}
                      >
                        Record: not the target
                      </button>
                      <button className="vbtn" disabled={busy} onClick={() => setRejectingId(null)}>
                        Cancel
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
            <div style={{ display: 'flex', gap: 10, marginTop: 12, alignItems: 'center' }}>
              <a
                className="vbtn"
                style={{ display: 'inline-block', textDecoration: 'none' }}
                href="/review"
              >
                Open chain review →
              </a>
              {decidedNames.length > 0 && (
                <button className="vbtn pri" onClick={onResolved}>
                  Next task →
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Discard (stuck + dead-letter) ── */}
        {(isStuck || isDeadLetter) && (
          <div className="vcard" style={{ padding: 16 }}>
            <div style={{ fontSize: 'var(--text-13)', fontWeight: 700 }}>Discard</div>
            <div style={{ fontSize: 'var(--text-12)', color: 'var(--muted)', marginTop: 4 }}>
              {isStuck
                ? "The document doesn't matter (duplicate, junk, out of scope). The failure record stays in the audit trail."
                : 'Close this failed write; the proposals stay decided and the record write is abandoned. Re-confirm through this pane if the changes are still wanted.'}
            </div>
            {!discardOpen ? (
              <button
                className="vbtn"
                style={{ marginTop: 12 }}
                onClick={() => setDiscardOpen(true)}
              >
                Discard…
              </button>
            ) : (
              <div style={{ marginTop: 12 }}>
                <input
                  autoFocus
                  placeholder="why is this safe to discard? (required)"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  style={{
                    width: '100%',
                    font: 'inherit',
                    fontSize: 'var(--text-12)',
                    padding: '7px 10px',
                    border: '1.5px solid var(--line)',
                    borderRadius: 7,
                  }}
                />
                <button
                  className="vbtn pri"
                  style={{ marginTop: 10 }}
                  onClick={() => act('discard')}
                  disabled={busy || !note.trim()}
                >
                  Discard task
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
